import React, { useState, useEffect } from 'react';
import { groupsAPI, websAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [webs, setWebs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [filter, setFilter] = useState({ web_id: '', search: '' });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    web_id: '',
    active: true,
    products: [{ product_code: '', description: '', price_with_iva: '', quantity: 1 }]
  });

  useEffect(() => {
    fetchGroups();
    fetchWebs();
  }, [filter.web_id]);

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getAll(filter);
      setGroups(response.data.groups);
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebs = async () => {
    try {
      const response = await websAPI.getActive();
      setWebs(response.data.webs);
    } catch (err) {
      console.error('Error fetching webs:', err);
    }
  };

  const openModal = async (group = null) => {
    if (group) {
      try {
        // Fetch full group details with products
        const response = await groupsAPI.getById(group.id);
        const fullGroup = response.data.group;
        setEditingGroup(fullGroup);
        setFormData({
          name: fullGroup.name,
          description: fullGroup.description || '',
          web_id: fullGroup.web_id || '',
          active: fullGroup.is_active,
          products: fullGroup.products && fullGroup.products.length > 0 ? fullGroup.products : [{ product_code: '', description: '', price_with_iva: '', quantity: 1 }]
        });
      } catch (err) {
        console.error('Error fetching group details:', err);
      }
    } else {
      setEditingGroup(null);
      setFormData({
        name: '',
        description: '',
        web_id: '',
        active: true,
        products: [{ product_code: '', description: '', price_with_iva: '', quantity: 1 }]
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { product_code: '', description: '', price_with_iva: '', quantity: 1 }]
    });
  };

  const removeProduct = (index) => {
    if (formData.products.length > 1) {
      setFormData({
        ...formData,
        products: formData.products.filter((_, i) => i !== index)
      });
    }
  };

  const updateProduct = (index, field, value) => {
    const updated = [...formData.products];
    updated[index][field] = value;
    setFormData({ ...formData, products: updated });
  };

  const calculateTotal = () => {
    return formData.products.reduce((sum, p) => {
      return sum + (parseFloat(p.price_with_iva) || 0) * (parseInt(p.quantity) || 0);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        products: formData.products.map(p => ({
          ...p,
          price_with_iva: parseFloat(p.price_with_iva),
          quantity: parseInt(p.quantity)
        }))
      };

      if (editingGroup) {
        await groupsAPI.update(editingGroup.id, data);
      } else {
        await groupsAPI.create(data);
      }
      closeModal();
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al guardar grupo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este grupo?')) return;
    try {
      await groupsAPI.delete(id);
      fetchGroups();
    } catch (err) {
      alert('Error al eliminar grupo');
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Grupos/Paquetes</h2>
          <p className="text-gray-600">Gestiona paquetes de productos predefinidos</p>
        </div>
        <button onClick={() => openModal()} className="btn btn-primary">
          + Nuevo Grupo
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Filtrar por Web</label>
            <select
              value={filter.web_id}
              onChange={(e) => setFilter({ ...filter, web_id: e.target.value })}
              className="input"
            >
              <option value="">Todas las webs</option>
              {webs.map(web => (
                <option key={web.id} value={web.id}>{web.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Buscar</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              onKeyUp={(e) => e.key === 'Enter' && fetchGroups()}
              placeholder="Nombre del grupo..."
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Groups List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No hay grupos creados
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(group => (
            <div key={group.id} className="bg-white rounded-lg shadow p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{group.name}</h3>
                  {group.web_name && (
                    <span className="text-xs text-primary-600">{group.web_name}</span>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs rounded ${group.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {group.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              {group.description && (
                <p className="text-sm text-gray-600 mb-3">{group.description}</p>
              )}
              <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                <span>{group.product_count} productos</span>
                <span className="font-semibold text-gray-900">
                  ${parseFloat(group.total_value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => openModal(group)}
                  className="flex-1 btn btn-secondary text-sm"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(group.id)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Web (opcional)</label>
                  <select
                    value={formData.web_id}
                    onChange={(e) => setFormData({ ...formData, web_id: e.target.value })}
                    className="input"
                  >
                    <option value="">Global (todas las webs)</option>
                    {webs.map(web => (
                      <option key={web.id} value={web.id}>{web.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Descripcion</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  rows="2"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="active" className="text-sm text-gray-700">Activo</label>
              </div>

              {/* Products */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="label">Productos</label>
                  <button type="button" onClick={addProduct} className="text-sm text-primary-600 hover:text-primary-700">
                    + Agregar producto
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.products.map((product, index) => (
                    <div key={index} className="border rounded p-3">
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-2">
                          <input
                            type="text"
                            value={product.product_code}
                            onChange={(e) => updateProduct(index, 'product_code', e.target.value)}
                            placeholder="Codigo"
                            className="input text-sm"
                          />
                        </div>
                        <div className="col-span-5">
                          <input
                            type="text"
                            value={product.description}
                            onChange={(e) => updateProduct(index, 'description', e.target.value)}
                            placeholder="Descripcion *"
                            className="input text-sm"
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            value={product.price_with_iva}
                            onChange={(e) => updateProduct(index, 'price_with_iva', e.target.value)}
                            placeholder="Precio"
                            className="input text-sm"
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            min="1"
                            value={product.quantity}
                            onChange={(e) => updateProduct(index, 'quantity', e.target.value)}
                            placeholder="Cant"
                            className="input text-sm"
                            required
                          />
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          {formData.products.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeProduct(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-right">
                  <span className="font-semibold">Total: ${calculateTotal().toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingGroup ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Groups;
